import React, {useEffect, useRef, useState, useCallback, useMemo} from 'react';
import classnames from 'classnames';
import {
    useTileTransitions,
    useTitleTransitions
} from '@dazn/lp-web/src/hooks/useTransitions';
import {animated} from '@react-spring/web';
import Markdown from 'markdown-to-jsx';
import {
    IContentTier,
    IContentTierBenefit,
    IContentTiers,
    TBillingPeriod
} from '@dazn/lp-web/src/types/contentTiers.types';
import {ContentTier} from '@dazn/lp-web/src/components/ContentTiers/ContentTier';
import BenefitsComponent from '@dazn/lp-web/src/components/ContentTiers/BenefitComponent';
import {getPPVContentTierTitle} from './contentTiersUtils';
import {useLandingPagesContext} from '../../contexts/LandingPageContext';
import styles from './content-tiers.module.css';
import PlanSelector from './ComparePlan/PlanSelector';
import {useSyncElementsHeight} from '../../hooks/useSyncElementsHeight';
import {FREE_TRIAL_VARIABLE, IOffer, IOffers} from '@dazn/lp-web/src/types';
import {useMedia} from '../../hooks/useMedia';
import classNames from 'classnames';
import {ArrowLeft} from '../../Icons/ArrowLeft';
import {ArrowRight} from '../../Icons/ArrowRight';
import {shouldSuperImpose, showSeeMorePlansUi} from '../Header/Utils/features';
import {ComponentTypes} from '@dazn/lp-web/src/constants/componentTypes.constants';
import WeeklyTier from './WeeklyTier';

interface IContentTiersProps {
    contentTiers: IContentTiers;
    key: number;
    disableAnimation: boolean;
}

interface IAnimatedContentProps {
    content: string | null | undefined;
    className: string;
    animations: any;
}

const AnimatedContent: React.FC<IAnimatedContentProps> = ({
    content,
    className,
    animations
}) => {
    if (!content) return null;

    return (
        <animated.div {...animations}>
            <Markdown className={className}>{content}</Markdown>
        </animated.div>
    );
};

const checkIfFreeTrial = (tiers: IContentTier[], offerObj: IOffers | null) => {
    if (!tiers || !offerObj || !offerObj?.Offers)
        return {hasFreeTrial: false, freeTrialDays: 0};

    const freeTrialObj = offerObj.Offers.find((offer: IOffer) =>
        tiers.some(
            (tier: IContentTier) =>
                tier?.entitlementSetId === offer?.EntitlementSetId &&
                tier?.billingPeriod === offer?.BillingPeriod &&
                offer?.FreeTrialDays &&
                offer.FreeTrialDays > 0
        )
    );

    return {
        hasFreeTrial: freeTrialObj ? true : false,
        freeTrialDays: freeTrialObj?.FreeTrialDays ?? 0
    };
};

export const applyGoldColor = (text: string) => {
    if (!text.includes('##')) {
        return text; // If no '##' found, return the original text
    }

    return text.replace(/##(.*?)##/g, `<span class="goldColor">$1</span>`);
};

const ContentTiers: React.FC<IContentTiersProps> = ({
    contentTiers,
    disableAnimation
}) => {
    const {offers, promoOffers, multiplePromoOffers} = useLandingPagesContext();
    const {hasFreeTrial, freeTrialDays} = checkIfFreeTrial(
        contentTiers?.tiers,
        offers
    );

    const contentTiersContainerRef = useRef(null);
    const contentTiersWrapperRef = useRef(null);
    const cardsContainerRef = useRef<null | HTMLDivElement>(null);
    const [refView, springs] = useTitleTransitions();
    const animations = disableAnimation ? {} : {style: springs, ref: refView};
    const [v3ClickedChevron, setV3ClickedChevron] = useState<number>(0);

    const [refCards, cardSprings] = useTileTransitions();
    const cardAnimations = disableAnimation
        ? {}
        : {style: cardSprings, ref: refCards};

    const [cardsContainerWidth, setCardsContainerWidth] = useState(0);
    const [cardWidth, setCardWidth] = useState(0);
    const [isFirstCardInView, setIsFirstCardInView] = React.useState(true);
    const [isLastCardInView, setIsLastCardInView] = React.useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [billingPlanSwitch, setBillingPlanSwitch] = useState<TBillingPeriod>(
        contentTiers?.defaultBillingPlan ?? 'Instalments'
    );
    useSyncElementsHeight(contentTiersWrapperRef);

    useEffect(() => {
        if (
            cardsContainerRef?.current &&
            showSeeMorePlansUi() &&
            contentTiers?.tiers?.length > 3
        ) {
            const highlightedCard = cardsContainerRef?.current?.querySelector(
                '[data-card-highlighted="true"]'
            );

            if (highlightedCard) {
                const rect = highlightedCard.getBoundingClientRect();
                const widthInView = (window.innerWidth - rect.width) / 2;
                const left = rect.left - widthInView + 32;
                cardsContainerRef?.current?.scrollTo({left});
            }
        }
    }, [contentTiers]);

    useEffect(() => {
        const isWeeklyBillingPeriod =
            contentTiers &&
            contentTiers?.tiers?.some(
                (tier) =>
                    tier.billingPeriod === 'Week' ||
                    contentTiers?.showSeeMorePlans
            );
        const noOfCards =
            contentTiers?.tiers?.length && isWeeklyBillingPeriod
                ? contentTiers?.tiers?.length - 1
                : contentTiers?.tiers?.length
                ? contentTiers?.tiers?.length
                : 0;
        const cardWidth =
            window.innerWidth < 768 ? window.innerWidth - 84 : 400;

        setCardWidth(cardWidth);
        setCardsContainerWidth(cardWidth * noOfCards + 16 * (noOfCards - 1));

        const handleResize = () => {
            const noOfCards = contentTiers?.tiers?.length ?? 0;
            setCardsContainerWidth(
                cardWidth * noOfCards + 16 * (noOfCards - 1)
            );
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [contentTiers]);
    let title = hasFreeTrial
        ? contentTiers?.freeTrialTitle?.includes(FREE_TRIAL_VARIABLE)
            ? contentTiers?.freeTrialTitle?.replace(
                  FREE_TRIAL_VARIABLE,
                  String(freeTrialDays)
              )
            : contentTiers?.freeTrialTitle
        : contentTiers?.title;
    const applyGoldTitle = title?.includes('#*');
    if (applyGoldTitle) {
        title = title?.replace('#*', '')?.replace('*#', '');
    }
    const {isDesktop} = useMedia();

    const toggleExpand = () => {
        setIsExpanded((prev) => !prev);
    };

    const isMobile = !isDesktop;

    const mobileDescription = contentTiers?.freeTrialMobileDescription;
    const formattedMobileDescription = mobileDescription?.includes('**')
        ? mobileDescription.replace(
              /\*\*(.*?)\*\*/g,
              `<span class="${styles.whiteTag}">$1</span>`
          )
        : mobileDescription;

    const description = hasFreeTrial
        ? isMobile
            ? formattedMobileDescription
            : contentTiers?.freeTrialDescription
        : contentTiers?.description;

    const shouldShowArrow = useCallback(
        (direction: string) => {
            const cardsContainer = document.getElementById(
                'cards-container-alib'
            );

            // Function to check if the first tierCard with ID "card-1" is completely in view
            function isFirstCardFullyInView() {
                const firstTierCard =
                    document.getElementById('card-item-alib-1');

                // Ensure both the container and the first tierCard exist
                if (!!cardsContainer && !!firstTierCard) {
                    // Get the bounding rectangles of the container and the first tierCard
                    const containerRect =
                        cardsContainer.getBoundingClientRect();
                    const cardRect = firstTierCard.getBoundingClientRect();

                    // Check if the left edge of the card is within the container's left and right edges
                    const isFullyInView =
                        cardRect.left >= containerRect.left &&
                        cardRect.right <= containerRect.right;
                    setIsFirstCardInView(isFullyInView);
                }
            }
            function isLastCardFullyInView() {
                const lastTierCard = document.getElementById(
                    'card-item-alib-' + contentTiers?.tiers?.length
                );

                if (cardsContainer && lastTierCard) {
                    // Get the bounding rectangles of the container and the last tierCard
                    const containerRect =
                        cardsContainer.getBoundingClientRect();
                    const cardRect = lastTierCard.getBoundingClientRect();

                    const isFullyInView = cardRect.right <= containerRect.right;
                    setIsLastCardInView(isFullyInView);
                }
            }
            if (direction === 'left') isFirstCardFullyInView();
            else isLastCardFullyInView();
        },
        [contentTiers?.tiers?.length]
    );

    useEffect(() => {
        // Check for the fist time/while window resizes and hide arrows if first and last cards are in view
        const timeoutId = setTimeout(() => {
            shouldShowArrow('left');
            shouldShowArrow('right');
        }, 0);
        return () => clearTimeout(timeoutId);
    }, [shouldShowArrow]);

    useEffect(() => {
        const handleScroll = () => {
            shouldShowArrow('left');
            shouldShowArrow('right');
        };
        const scrollableElement = document.getElementById(
            'cards-container-alib'
        );

        if (scrollableElement) {
            // Add event listener when component mounts
            scrollableElement.addEventListener('scroll', handleScroll, {
                passive: true
            });

            // Clean up: remove event listener when component unmounts
            return () => {
                scrollableElement.removeEventListener('scroll', handleScroll);
            };
        }
    }, [shouldShowArrow]);

    const handleArrowClick = (scrollDir: string) => {
        const element = document.getElementById('cards-container-alib');
        shouldShowArrow('left');
        shouldShowArrow('right');
        if (element) {
            let scrollAmount = 250; // Adjust this value as needed
            const highlightedCard = cardsContainerRef?.current?.querySelector(
                '[data-card-highlighted="true"]'
            );
            if (highlightedCard) {
                const rect = highlightedCard.getBoundingClientRect();
                scrollAmount = rect?.width;
            }
            if (scrollDir === 'left') {
                element.scrollBy({
                    left: -scrollAmount,
                    behavior: 'smooth'
                });
            } else if (scrollDir === 'right') {
                element.scrollBy({
                    left: scrollAmount,
                    behavior: 'smooth'
                });
            }

            //handleScrollGradient(element);
        } else {
            return null;
        }
    };
    const showWeeklyPlanAtTop =
        contentTiers?.showWeeklyTierTop && !contentTiers?.showWeeklyTierAside;
    const showWeeklyPlanAtBottom =
        !contentTiers?.showWeeklyTierAside && !showWeeklyPlanAtTop;
    const sliceCount = contentTiers?.tiers?.length - 1;
    const isShowSeeMorePlansUi =
        contentTiers && contentTiers?.showSeeMorePlans && showSeeMorePlansUi();

    const contentTiersArrray = isShowSeeMorePlansUi
        ? contentTiers?.tiers?.slice(0, sliceCount)
        : contentTiers?.tiers;

    const remainingContentTiers =
        (isShowSeeMorePlansUi &&
            contentTiers?.tiers?.slice(
                sliceCount,
                contentTiers?.tiers?.length
            )) ||
        [];

    const lengthToDisplayArrow =
        contentTiers &&
        contentTiers?.tiers &&
        contentTiers?.tiers.some((tier) => tier.billingPeriod === 'Week')
            ? 3
            : 2;
    const version = contentTiers?.version;
    const style = contentTiers?.backgroundImage?.src
        ? {backgroundImage: `url(${contentTiers.backgroundImage.src})`}
        : {};

    let updatedTitle = title;
    let priceToShow = '';

    if (version !== 'v1' && title?.includes('{price}')) {
        const price = getPPVContentTierTitle({
            title,
            offers,
            entitlementSetId: contentTiers?.entitlementSetId
        });
        updatedTitle = price?.updatedTitle;
        priceToShow = price?.price;
    }

    const isBoxingPage = useMemo(() => {
        return window.location.pathname?.split('/')?.includes('boxing');
    }, [window.location.pathname]);

    const handleIndexUpdate = (newIndex: number): void => {
        setV3ClickedChevron(newIndex);
    };

    return (
        <div id={ComponentTypes.CONTENT_TIERS} ref={contentTiersWrapperRef}>
            <div
                ref={contentTiersContainerRef}
                className={classNames(styles.contentTiersContainer, {
                    [styles.tiersImposed]: shouldSuperImpose(),
                    [styles.newPlanUi]: contentTiers?.showPlanUI,
                    [styles.reducePaddingNewUI]: contentTiers?.showPlanUI,
                    [styles.removePaddingNewUI]:
                        contentTiers?.showPlanUI &&
                        !contentTiers?.title &&
                        !contentTiers?.description,
                    [styles.contentTiersContainerV2]:
                        version !== 'v1' && isBoxingPage,
                    [styles.weeklyTopContentTier]: showWeeklyPlanAtTop
                })}
                style={style}
            >
                {!isMobile &&
                    contentTiers?.tiers?.length &&
                    contentTiers?.tiers?.length > lengthToDisplayArrow &&
                    !isShowSeeMorePlansUi && (
                        <>
                            {!isFirstCardInView && (
                                <div
                                    className={styles.leftArrow}
                                    onClick={() => handleArrowClick('left')}
                                >
                                    <ArrowLeft />
                                </div>
                            )}
                            {!isLastCardInView && (
                                <div
                                    className={styles.rightArrow}
                                    onClick={() => handleArrowClick('right')}
                                >
                                    <ArrowRight fill="#F9FAFA" />
                                </div>
                            )}
                        </>
                    )}
                {contentTiers && (
                    <div
                        className={classnames(styles.content, {
                            [styles.contentCentered]:
                                cardsContainerWidth < window.innerWidth,
                            [styles.contentLeftAligned]:
                                cardsContainerWidth > window.innerWidth,
                            [styles.contentCenterAligned]: isShowSeeMorePlansUi
                        })}
                    >
                        {updatedTitle && (
                            <AnimatedContent
                                content={updatedTitle}
                                className={classnames(styles.title, {
                                    [styles.goldColor]: applyGoldTitle,
                                    [styles.titleNewUi]:
                                        contentTiers?.showPlanUI,
                                    [styles.contentTierTitleV2]:
                                        version !== 'v1'
                                })}
                                animations={animations}
                            />
                        )}
                        {description && (
                            <AnimatedContent
                                content={applyGoldColor(description)}
                                className={classnames(styles.description, {
                                    [styles.descriptionNewUi]:
                                        contentTiers?.showPlanUI,
                                    [styles.contentTierDescriptionV2]:
                                        version !== 'v1'
                                })}
                                animations={animations}
                            />
                        )}

                        {contentTiers &&
                            contentTiers.tiers &&
                            contentTiers.tiers.some(
                                (tier) => tier.billingPeriod === 'Week'
                            ) &&
                            showWeeklyPlanAtTop &&
                            contentTiers.tiers?.map(
                                (contentTier: IContentTier, index) => {
                                    return (
                                        <WeeklyTier
                                            key={index}
                                            contentTier={contentTier}
                                            offers={offers}
                                            promoOffers={promoOffers}
                                            multiplePromoOffers={
                                                multiplePromoOffers
                                            }
                                            showWeeklyPlanAtTop={
                                                showWeeklyPlanAtTop
                                            }
                                            bottomDividerText={
                                                contentTiers?.bottomDividerText ||
                                                ''
                                            }
                                        />
                                    );
                                }
                            )}
                        {contentTiers?.showBillingPeriodSwitcher && (
                            <animated.div {...animations}>
                                <PlanSelector
                                    setBillingPlan={setBillingPlanSwitch}
                                    billingPlanSwitch={billingPlanSwitch}
                                    billingPlans={contentTiers?.billingPlans}
                                />
                            </animated.div>
                        )}
                        {contentTiers?.showBenefits &&
                            contentTiers?.benefits &&
                            contentTiers?.benefits?.length > 0 && (
                                <div
                                    className={classnames(
                                        styles.benefitsContainer,
                                        styles.tiersBenefitsContainer
                                    )}
                                >
                                    {contentTiers?.benefits?.map(
                                        (
                                            benefit: IContentTierBenefit,
                                            index: number
                                        ) => (
                                            <BenefitsComponent
                                                key={index + 1}
                                                benefit={benefit}
                                                showPlanUI={
                                                    contentTiers?.showPlanUI ??
                                                    false
                                                }
                                                priceToShow={priceToShow}
                                            />
                                        )
                                    )}
                                </div>
                            )}
                        <animated.div
                            {...cardAnimations}
                            ref={(ref) => {
                                cardsContainerRef.current = ref;

                                if (cardAnimations.ref) {
                                    cardAnimations.ref.current = ref;
                                }
                            }}
                            id="cards-container-alib"
                            className={classnames(styles.cardsContainer, {
                                [styles.cardsContainerNewUI]:
                                    contentTiers?.showPlanUI &&
                                    showSeeMorePlansUi(),
                                [styles.cardsContainerNewUIHorizontal]:
                                    contentTiers?.showPlanUI &&
                                    !showSeeMorePlansUi(),
                                [styles.addBlockForSinglePlans]:
                                    contentTiers.showPlanUI,
                                [styles.moreThan3Cards]:
                                    contentTiersArrray?.length > 3 &&
                                    (!showWeeklyPlanAtBottom ||
                                        !showWeeklyPlanAtTop),
                                [styles.cardContainersMobileV2]:
                                    version !== 'v1'
                            })}
                        >
                            {contentTiersArrray?.map(
                                (contentTier: IContentTier, index: number) => (
                                    <ContentTier
                                        key={index + 1}
                                        contentTier={contentTier}
                                        cardWidth={cardWidth}
                                        bestValueBadgeShown={
                                            contentTiers?.bestValueBadgeShown
                                        }
                                        planSelectorShown={
                                            contentTiers?.showBillingPeriodSwitcher
                                        }
                                        preselectBillingPeriod={
                                            contentTiers.preselectBillingPeriod
                                        }
                                        position={index + 1}
                                        billingPlan={billingPlanSwitch}
                                        showPlanUI={contentTiers.showPlanUI}
                                        remainingContentTiers={
                                            contentTiersArrray.length >= 3 &&
                                            isShowSeeMorePlansUi
                                        }
                                        isHighlightedMiddleTier={
                                            contentTiersArrray.length >= 3 &&
                                            index === 1 &&
                                            isShowSeeMorePlansUi
                                        }
                                        showWeeklyPlanAtBottom={
                                            showWeeklyPlanAtBottom
                                        }
                                        showWeeklyPlanAtTop={
                                            showWeeklyPlanAtTop
                                        }
                                        version={version}
                                        priceToShow={priceToShow}
                                        index={index}
                                        v3ClickedChevron={v3ClickedChevron}
                                        onV3ChevronClick={handleIndexUpdate}
                                    />
                                )
                            )}
                        </animated.div>
                        {isShowSeeMorePlansUi && (
                            <>
                                <div
                                    className={styles.seeMorePlans}
                                    onClick={toggleExpand}
                                >
                                    {isExpanded
                                        ? contentTiers?.closeSeeMoreText
                                        : contentTiers?.seeMorePlansText}{' '}
                                    {isExpanded ? (
                                        <span
                                            style={{
                                                paddingInlineStart: '5px',
                                                position: 'relative',
                                                top: '4px'
                                            }}
                                        >
                                            &#x2303;
                                        </span>
                                    ) : (
                                        <span
                                            style={{
                                                paddingInlineStart: '5px',
                                                position: 'relative',
                                                top: '-3px'
                                            }}
                                        >
                                            &#x2304;
                                        </span>
                                    )}
                                </div>
                                {isExpanded && (
                                    <animated.div
                                        {...cardAnimations}
                                        className={classnames(
                                            styles.cardsContainer,
                                            {
                                                [styles.cardsContainerNewUI]:
                                                    contentTiers?.showPlanUI,
                                                [styles.addBlockForSinglePlans]:
                                                    contentTiers.showPlanUI,
                                                [styles.cardsContainerNewUIPadding]:
                                                    contentTiers?.showPlanUI,
                                                [styles.centralisedCardsContainer]:
                                                    contentTiers?.showPlanUI &&
                                                    remainingContentTiers.length ===
                                                        1
                                            }
                                        )}
                                    >
                                        {remainingContentTiers?.map(
                                            (
                                                contentTier: IContentTier,
                                                index: number
                                            ) => (
                                                <ContentTier
                                                    key={index + 1}
                                                    contentTier={contentTier}
                                                    cardWidth={cardWidth}
                                                    bestValueBadgeShown={
                                                        contentTiers?.bestValueBadgeShown
                                                    }
                                                    planSelectorShown={
                                                        contentTiers?.showBillingPeriodSwitcher
                                                    }
                                                    preselectBillingPeriod={
                                                        contentTiers.preselectBillingPeriod
                                                    }
                                                    billingPlan={
                                                        billingPlanSwitch
                                                    }
                                                    showPlanUI={
                                                        contentTiers.showPlanUI
                                                    }
                                                    remainingContentTiers={true}
                                                />
                                            )
                                        )}
                                    </animated.div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
            {contentTiers &&
                contentTiers.tiers &&
                contentTiers.tiers.some(
                    (tier) => tier.billingPeriod === 'Week'
                ) &&
                showWeeklyPlanAtBottom &&
                contentTiers.tiers?.map((contentTier: IContentTier, index) => {
                    return (
                        <WeeklyTier
                            key={index}
                            contentTier={contentTier}
                            offers={offers}
                            promoOffers={promoOffers}
                            multiplePromoOffers={multiplePromoOffers}
                        />
                    );
                })}
        </div>
    );
};

export default ContentTiers;
